import React, { ChangeEvent, useState, useEffect } from 'react';
import './_fullAddress.scss';

import FormControl from 'shared/FormControl/FormControl';
import {
  FlatNumber,
  BuildingName,
  BuildingNumber,
  City,
  Street,
  PostCode,
  County,
} from 'components/Forms/Address/Fields';
import { AddressType } from '../types';
import { formatAddress, fetchAddresses } from 'utils/address';
import { AddressFieldsValueMethod } from 'utils/address/form';
import { formatPostcode } from 'utils/address';
import { FormikContextType, useFormikContext } from 'formik';

export { FullAddressValidator as Validator } from './Validator';

interface IFullAddressFields {
  prefix: string;
  title?: string;
  isValid: (fieldName: string) => boolean;
  isCorrect: (fieldName: string) => boolean;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  labels?: {
    postcode?: string;
    flatNumber?: string;
    buildingName?: string;
    buildingNumber?: string;
    street?: string;
    city?: string;
    county?: string;
    houseNumber?: string;
  };
  requiredFields: any;
  header?: string;
  handleBlur?: (name: string) => (event: ChangeEvent<HTMLInputElement>) => void;
  onSelect: AddressFieldsValueMethod;
  showFlatNumber: boolean;
  useRebrand?: boolean;
  wrapperClass?: string;
}

export const FullAddress: React.FC<IFullAddressFields> = ({
  prefix,
  isCorrect,
  isValid,
  labels,
  requiredFields = {},
  disabled,
  header = 'Full address',
  handleBlur,
  onSelect,
  wrapperClass,
  showFlatNumber = true,
  useRebrand = false,
}: IFullAddressFields) => {
  const [relatedAddresses, setRelatedAddresses] = useState<AddressType[]>([]);
  const { values }: FormikContextType<any> = useFormikContext();
  const { postcode } = values[prefix];

  const fullAddressLabels = {
    flatNumber: 'Flat number',
    buildingName: 'Building name',
    buildingNumber: 'Building number',
    street: 'Street',
    city: 'City',
    county: 'County',
    postcode: 'Post Code',
    ...labels,
  };

  const selectAddressHandler = (address: AddressType) => {
    return () => onSelect(prefix, address);
  };

  const handlePostcodeBlur = (e: ChangeEvent<HTMLInputElement>) => {
    if (handleBlur) {
      handleBlur('postcode')(e);
    }

    fetchAddresses(e.target.value).then((relatedAddresses) => {
      setRelatedAddresses(relatedAddresses);
    });
  };

  useEffect(() => {
    if (postcode) {
      const formattedPostcode = formatPostcode(postcode);

      fetchAddresses(formattedPostcode).then((relatedAddresses) => {
        setRelatedAddresses(relatedAddresses);
      });
    }
    // eslint-disable-next-line
  }, []);

  return (
    <div className={`full-address ${wrapperClass}`}>
      <div className={`full-address__postcode ${wrapperClass}`}>
        <PostCode
          prefix={prefix}
          isValid={isValid}
          isCorrect={isCorrect}
          label={fullAddressLabels.postcode}
          required={requiredFields.postCode}
          disabled={disabled}
          handleBlur={handlePostcodeBlur}
          useRebrand={useRebrand}
        />
      </div>

      {relatedAddresses && relatedAddresses.length > 0 && (
        <div className={`${wrapperClass}`}>
          <FormControl showErrorIcon={false}>
            <select className="full-address__address-picker" multiple={true}>
              {relatedAddresses.map((address: AddressType, index: number) => (
                <option key={`${prefix}-postcode-related-addresses-${index}`} onClick={selectAddressHandler(address)}>
                  {formatAddress(address)}
                </option>
              ))}
            </select>
          </FormControl>
        </div>
      )}

      <div className={`full-address__details ${wrapperClass}`}>
        <div className={`${wrapperClass}__label`}>
          <FormControl labelText={header} required />
        </div>
        {showFlatNumber && (
          <FlatNumber
            prefix={prefix}
            isValid={isValid}
            isCorrect={isCorrect}
            label={fullAddressLabels.flatNumber}
            required={requiredFields.flatNumber}
            disabled={disabled}
            handleBlur={handleBlur ? handleBlur('flatNumber') : undefined}
            useRebrand={useRebrand}
          />
        )}
        <BuildingNumber
          prefix={prefix}
          isValid={isValid}
          isCorrect={isCorrect}
          label={fullAddressLabels.buildingNumber}
          required={requiredFields.buildingNumber}
          disabled={disabled}
          handleBlur={handleBlur ? handleBlur('buildingNumber') : undefined}
          useRebrand={useRebrand}
        />
        <BuildingName
          prefix={prefix}
          isValid={isValid}
          isCorrect={isCorrect}
          label={fullAddressLabels.buildingName}
          required={requiredFields.buildingName}
          disabled={disabled}
          handleBlur={handleBlur ? handleBlur('buildingName') : undefined}
          useRebrand={useRebrand}
        />
        <Street
          prefix={prefix}
          isValid={isValid}
          isCorrect={isCorrect}
          label={fullAddressLabels.street}
          required={requiredFields.street}
          disabled={disabled}
          handleBlur={handleBlur ? handleBlur('street') : undefined}
          useRebrand={useRebrand}
        />
        <City
          prefix={prefix}
          isValid={isValid}
          isCorrect={isCorrect}
          label={fullAddressLabels.city}
          required={requiredFields.city}
          disabled={disabled}
          handleBlur={handleBlur ? handleBlur('city') : undefined}
          useRebrand={useRebrand}
        />
        <County
          prefix={prefix}
          isValid={isValid}
          isCorrect={isCorrect}
          label={fullAddressLabels.county}
          required={requiredFields.county}
          disabled={disabled}
          handleBlur={handleBlur ? handleBlur('county') : undefined}
          useRebrand={useRebrand}
        />
      </div>
    </div>
  );
};

export default FullAddress;
